import React from 'react';
import { Icon } from '@/modules/Icon';
import { Avatar } from '@/modules/Avatar';
import { InnerButton } from '@/modules/InnerButton';
import { useAlertStyles } from '@/modules/Alert/hooks/useAlertStyles';
import { AlertHeaderProps } from '@/shared/types/alert';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils/cn';

/**
 * AlertHeader - Cabecera del Alert
 *
 * Tokens semánticos de Figma:
 * - Gap: 8px (space_8) entre icono y contenido
 * - Título: font-body-md-bold
 * - Mensaje: font-body-xs-regular
 * - Icono: 20px
 */

export const AlertHeader = React.memo((props: AlertHeaderProps) => {
  const { getIconForType } = useAlertStyles();

  const isOutlined = props.variant === 'outlined';
  const truncateOneLine = props.truncateHeaderText
    ? 'ui-block ui-w-full ui-max-w-full ui-min-w-0 ui-overflow-hidden ui-text-ellipsis ui-whitespace-nowrap'
    : '';

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (props.onClose) {
      props.onClose(e);
    }
  };

  const handlePrimaryClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    props.onPrimaryClick?.();
  };

  const handleSecondaryClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    props.onSecondaryClick?.();
  };

  const leadingVisual = props.avatar ? (
    props.avatarIntentBadge ? (
      <div className="ui-relative ui-inline-flex ui-shrink-0">
        <Avatar type="people" src={props.avatar} size="xs" className="ui-shrink-0" />
        <div
          className="ui-absolute ui-bottom-[-5px] ui-right-[-5px] ui-flex ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-fill ui-p-4 ui-shadow-01"
          aria-hidden
        >
          <span className="ui-leading-none ui-text-brand">
            <Icon
              name={getIconForType(props.color || 'info')}
              color="current"
              size="xs"
              className="ui-block ui-text-[10px] ui-leading-none"
            />
          </span>
        </div>
      </div>
    ) : (
      <Avatar type="people" src={props.avatar} size="xs" className="ui-shrink-0" />
    )
  ) : isOutlined ? (
    <Icon
      name={getIconForType(props.color || 'danger')}
      color={props.color}
      size="sm"
      className="ui-shrink-0"
      background
      backgroundColor={props.color || 'danger'}
      backgroundSize="sm"
    />
  ) : (
    <div
      className="ui-shrink-0"
      style={{
        color: `var(--ui-color-alert-${props.color || 'danger'}-text)`,
      }}
    >
      <Icon name={getIconForType(props.color || 'danger')} color="current" size="sm" />
    </div>
  );

  return (
    <div className="ui-flex ui-w-full ui-min-w-0 ui-flex-col ui-items-start ui-justify-between ui-gap-16">
      <div className="ui-flex ui-w-full ui-min-w-0 ui-items-start ui-gap-8">
        <div className="ui-flex ui-w-fit ui-max-w-full ui-shrink-0 ui-flex-col ui-items-start ui-self-start">
          {leadingVisual}
        </div>
        <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-2">
          <div
            className={cn(
              'ui-flex ui-w-full ui-min-w-0 ui-justify-between ui-gap-8',
              props.endAdornment && !props.showCloseButton ? 'ui-items-center' : 'ui-items-start'
            )}
          >
            <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-0">
              {props.title !== null && (
                <div
                  title={props.truncateHeaderText && props.title ? props.title : undefined}
                  className={cn(
                    'ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm',
                    isOutlined && 'ui-text-primary',
                    truncateOneLine
                  )}
                >
                  {props.title}
                </div>
              )}
              <div
                title={props.truncateHeaderText && props.message ? props.message : undefined}
                className={cn(
                  'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
                  isOutlined && 'ui-text-secondary',
                  !props.truncateHeaderText && 'ui-text-pretty',
                  truncateOneLine
                )}
              >
                {props.message}
              </div>

              {props.description !== null && props.description !== '' && (
                <div
                  title={
                    props.truncateHeaderText && props.description ? props.description : undefined
                  }
                  className={cn(
                    'ui-mt-2 ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs',
                    isOutlined && 'ui-text-primary',
                    !props.truncateHeaderText && 'ui-text-pretty',
                    truncateOneLine
                  )}
                >
                  {props.description}
                </div>
              )}
            </div>

            {props.showCloseButton ? (
              <InnerButton
                icon={faXmark}
                size="xxs"
                pill="pill"
                color="tertiary"
                className="ui-shrink-0"
                onClick={handleClose}
                aria-label="Cerrar alerta"
              />
            ) : props.endAdornment ? (
              <div className="ui-flex ui-shrink-0 ui-items-center">{props.endAdornment}</div>
            ) : null}
          </div>

          {props.headerAddon ? (
            <div className="ui-flex ui-w-full ui-min-w-0 ui-max-w-full ui-flex-col ui-gap-2">
              {props.headerAddon}
            </div>
          ) : null}
        </div>
      </div>
      {(props.onSecondaryClick || props.onPrimaryClick) && (
        <div className="ui-flex ui-w-full ui-items-center ui-justify-end ui-gap-1.5">
          {props.onSecondaryClick && (
            <InnerButton
              clickable={true}
              theme={'solid'}
              color={'tertiary'}
              size="xs"
              onClick={handleSecondaryClick}
            >
              {props.secondaryButtonText || ''}
            </InnerButton>
          )}
          {props.onPrimaryClick && (
            <InnerButton
              clickable={true}
              theme={'solid'}
              color={'secondary'}
              size="xs"
              onClick={handlePrimaryClick}
            >
              {props.primaryButtonText || ''}
            </InnerButton>
          )}
        </div>
      )}
    </div>
  );
});

AlertHeader.displayName = 'AlertHeader';
